<template>
  <BaseLabel
    :inputId="inputId"
    :label="label || ''"
    :labelAlignment="labelAlignment"
    :additionalData="additionalData"
    :secondaryLabel="secondaryLabel"
    :required="required"
    dense>
    <div class="tw-flex">
      <input
        ref="inputRef"
        :class="`${disabled ? 'tw-pointer-events-none' : ''}  ${additionalClasses} tw-input-base ${dense ? 'tw-py-3 tw-my-0' : ''} ${
          inputType === 'checkbox' ? 'tw-w-4 tw-h-4' : ''
        }`"
        :id="inputId"
        :type="inputType"
        :step="step"
        :min="min"
        :max="max"
        :disabled="disabled"
        :value="modelValue"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :list="`${label}_datalist`"
        :required="required"
        :checked="checked"
        :data-test="dataTest"
        :style="inputType === 'checkbox' ? `transform: scale(${checkBoxScale || 1.5});` : ''"
        @input="handleInput"
        @focus="(event) => emit('focus', event)"
        @blur="(event) => emit('blur', event)"
        @keydown="(event) => emit('keydown', event)"
        :isValid="isValid"
        lang="de" />
      <!--
         lang="de" attribute is intentionally hard coded.
         Otherwise there will be an issue with the remote control inputs because browsers handle the decimal separator differently.
         On Chrome based browsers the default is a comma but on Firefox the default is dot.
         Also Firefox considers the locals for deciding the default.
         To keep things consistent in cross-browser use the same decimal separator
        -->
      <button
        type="button"
        v-if="props.actionIcon"
        class="tw-block tw-bg-transparent tw-px-4 tw-py-2 tw-aspect-square hover:tw-bg-slate-200 tw-border tw-border-solid tw-border-[#e3e4e8] hover:tw-border-b-[#ec7c16]"
        @click="emit('actionClick')">
        <FontAwesomeIcon :icon="props.actionIcon" />
      </button>
    </div>
    <datalist
      :id="`${label}_datalist`"
      v-if="dataList && dataList.length > 0">
      <option
        v-for="(tag, index) in dataList"
        :key="index"
        :value="tag" />
    </datalist>
    <div v-if="hasSlotData">
      <slot />
    </div>
  </BaseLabel>
</template>
<script setup lang="ts">
import { computed, onMounted, PropType, ref } from "vue";
import BaseLabel from "../BaseLabel/BaseLabel.vue";
import { BaseLabelProps } from "../BaseLabel/BaseLabelUtils";
import { BaseInputTypes } from "./BaseInputUtils";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const slots = defineSlots<{
  default(): any;
}>();
const hasSlotData = computed(() => !!slots.default);

const inputRef = ref<null | HTMLInputElement>(null);
const focus = () => {
  inputRef.value?.focus();
};

const select = () => {
  inputRef.value?.select();
};

defineExpose({
  focus,
  select,
  inputRef,
});

const emit = defineEmits<{
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
  keydown: [event: KeyboardEvent];
  // TODO: use defineModel in the future
  // TODO: use generic based on props.modelValue type in the future
  "update:modelValue": [value: any];
  actionClick: [];
}>();

const handleInput = (event: Event) => {
  const target = event.target;
  if (props.inputType === "checkbox") {
    emit("update:modelValue", (target as HTMLInputElement).checked);
  } else if (target instanceof HTMLInputElement) {
    emit("update:modelValue", convertStringValueToType(target.value));
  }
};

function convertStringValueToType(val: string) {
  if (props.inputType === "number") {
    if (val === "") return Number.NaN;
    return Number(val);
  }
  return String(val);
}

const props = defineProps({
  inputType: {
    type: String as PropType<BaseInputTypes>,
    required: true,
  },
  step: {
    type: Number,
    required: false,
  },
  min: {
    type: Number,
    required: false,
  },
  max: {
    type: Number,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
  placeholder: {
    type: String,
    required: false,
  },
  autocomplete: {
    type: String,
    required: false,
  },
  modelValue: {
    type: [String, Boolean, Number],
    required: true,
  },
  dataList: {
    type: Array as PropType<string[]>,
    required: false,
  },
  required: {
    type: Boolean,
    required: false,
  },
  value: {
    type: String,
    required: false,
  },
  checked: {
    type: Boolean,
    required: false,
  },
  checkBoxScale: {
    type: Number,
    required: false,
  },
  isValid: {
    type: Boolean,
    required: false,
    default: undefined,
  },
  dataTest: {
    type: String,
    default: "",
  },
  additionalClasses: {
    type: String,
    default: "",
  },
  focusOnMounted: {
    type: Boolean,
    default: false,
  },
  dense: {
    type: Boolean,
    default: false,
  },
  /** if set, displays a button next to the input field */
  actionIcon: {
    type: String,
  },
  ...BaseLabelProps,
});

onMounted(() => {
  if (props.focusOnMounted && inputRef.value != null) {
    inputRef.value.focus();
    inputRef.value.select();
  }
});
</script>
